import { PasswordInput, TextInput } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import defaultLogo from '../../../assets/images/telloport_logo_horizontal.png';
import { useAcceptAccountInvite } from './use-accept-account-invite';

const ID = 'accept-account-invite-page';

const AcceptAccountInvitePage = () => {
    const viewProps = useAcceptAccountInvite();
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);

    if (!viewProps.isValidHash) {
        return (
            <div
                id={ID}
                className='relative flex h-screen w-screen flex-col'>
                <div className='bg-brand-structure absolute top-0 right-0 left-0 -z-1 h-[40vh] w-full'></div>
                <div className='bg-background-surface mx-6 my-40 flex flex-col items-center gap-6 rounded-lg p-6 shadow-lg md:mx-auto md:w-1/2'>
                    <p className='text-foreground-default text-center text-base'>
                        <FormattedMessage
                            id='platform.standalone.accept-account-invite.invalid-link'
                            description='Error shown when the accept account invite link is invalid or expired'
                            defaultMessage='This invitation link is invalid. Please contact your administrator for a new invite.'
                        />
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            id={ID}
            className='relative flex h-screen w-screen flex-col'>
            <div className='bg-brand-structure absolute top-0 right-0 left-0 -z-1 h-[40vh] w-full'></div>
            <form
                className='bg-background-surface mx-6 my-40 flex flex-col items-center gap-6 rounded-lg p-6 shadow-lg md:mx-auto md:w-1/2'
                onSubmit={(e) => {
                    void viewProps.onSubmit(e);
                }}>
                <img
                    src={logo}
                    className='img-fluid mx-4 my-12 w-44'
                    alt='Logo'
                />

                <div className='flex w-full flex-col gap-4'>
                    <h1 className='text-foreground-default text-2xl font-medium'>
                        <FormattedMessage
                            id='platform.standalone.accept-account-invite.title'
                            description='Title on the accept account invite standalone page'
                            defaultMessage='Accept invitation'
                        />
                    </h1>

                    <TextInput
                        id='accept-account-invite-account-name'
                        required
                        label={
                            <FormattedMessage
                                id='platform.standalone.accept-account-invite.account-name'
                                description='Label for the new organization account name on the accept account invite page'
                                defaultMessage='Account name'
                            />
                        }
                        value={viewProps.formValues.account_name}
                        onChange={(value) => viewProps.setFormValues((prev) => ({ ...prev, account_name: value }))}
                    />

                    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                        <TextInput
                            id='accept-account-invite-first-name'
                            required
                            label={CommonMessage.OBJECTS.USER.FIRST_NAME}
                            value={viewProps.formValues.first_name}
                            onChange={(value) => viewProps.setFormValues((prev) => ({ ...prev, first_name: value }))}
                        />
                        <TextInput
                            id='accept-account-invite-last-name'
                            required
                            label={CommonMessage.OBJECTS.USER.LAST_NAME}
                            value={viewProps.formValues.last_name}
                            onChange={(value) => viewProps.setFormValues((prev) => ({ ...prev, last_name: value }))}
                        />
                    </div>

                    <hr className='border-border-default my-2 w-full border-t' />
                    <PasswordInput
                        id='accept-account-invite-password'
                        required
                        strength
                        noAutoComplete
                        noConditionedStyling
                        label={CommonMessage.FORMS.PASSWORD}
                        value={viewProps.formValues.password}
                        onChange={(value) =>
                            viewProps.setFormValues((prev) => ({
                                ...prev,
                                password: value,
                                confirm_password: value ? prev.confirm_password : ''
                            }))
                        }
                        isValidCallback={viewProps.setPasswordValid}
                    />
                    <PasswordInput
                        id='accept-account-invite-confirm-password'
                        required
                        noAutoComplete
                        noConditionedStyling
                        label={
                            <FormattedMessage
                                id='basic-auth-update.confirm-password'
                                description='Label for confirming the new basic auth password'
                                defaultMessage='Repeat password'
                            />
                        }
                        value={viewProps.formValues.confirm_password}
                        onChange={(value) => viewProps.setFormValues((prev) => ({ ...prev, confirm_password: value }))}
                        isValidCallback={viewProps.setConfirmPasswordValid}
                        toRepeat={viewProps.formValues.password}
                    />
                    {!viewProps.passwordsMatch && viewProps.formValues.confirm_password && (
                        <div className='text-status-danger text-sm'>
                            <FormattedMessage
                                id='basic-auth-update.passwords-must-match'
                                description='Validation message when repeated basic auth password does not match'
                                defaultMessage='Passwords must match.'
                            />
                        </div>
                    )}
                </div>

                <div className='flex w-full flex-col content-center items-center gap-3'>
                    <button
                        id='submit-button'
                        type='submit'
                        data-testid='acceptAccountInviteButton'
                        className='bg-brand-primary hover:bg-brand-secondary inline-flex w-1/3 items-center justify-center gap-2.5 rounded-md p-3.5 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
                        disabled={viewProps.submitDisabled || viewProps.isSubmitting}>
                        <div className='text-background-surface justify-start text-base font-medium'>{CommonMessage.BUTTONS.SUBMIT}</div>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AcceptAccountInvitePage;
